import React, { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import SidebarHeader from './sidebar/SidebarHeader';
import SidebarNavItem from './sidebar/SidebarNavItem';
import { useRouteFiltering } from './sidebar/useRouteFiltering';
import { usePendingApprovals } from '@/hooks/finance/usePaymentRequests';
import SidebarSearch from './sidebar/SidebarSearch';

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar }) => {
  const { userRole } = useAuth();
  const { data: pendingApprovals } = usePendingApprovals();
  const [searchQuery, setSearchQuery] = useState('');

  // Get pending approvals count
  const pendingApprovalsCount = pendingApprovals?.length || 0;

  // Get filtered routes based on user role and access configuration
  const { filteredRoutes } = useRouteFiltering(userRole);

  // Add badge counts and search filter
  const routesToDisplay = useMemo(() => {
    let result = filteredRoutes.map(route => {
      if (route.path === '/my-approvals') {
        return { ...route, badgeCount: pendingApprovalsCount };
      }
      return route;
    });

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(route =>
        route.name.toLowerCase().includes(query) ||
        (route.children && route.children.some(child => child.name.toLowerCase().includes(query)))
      );
    }

    return result;
  }, [filteredRoutes, pendingApprovalsCount, searchQuery]);


  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-40 flex flex-col transition-all duration-300 ease-in-out border-r shadow-xl",
        "bg-sidebar/80 backdrop-blur-xl border-sidebar-border/50",
        isOpen ? "w-64" : "w-20"
      )}
    >
      <SidebarHeader isOpen={isOpen} toggleSidebar={toggleSidebar} />

      <SidebarSearch onSearch={setSearchQuery} isOpen={isOpen} />

      <ScrollArea className="flex-1 px-3">
        <nav className="flex flex-col gap-1.5 py-2">
          {routesToDisplay.map((route) => (
            <SidebarNavItem
              key={route.path || route.name}
              route={route}
              isOpen={isOpen}
            />
          ))}

          {routesToDisplay.length === 0 && searchQuery && (
            <div className="px-4 py-8 text-center text-sm text-sidebar-foreground/50 italic">
              No results found
            </div>
          )}
        </nav>

      </ScrollArea>

      {isOpen && (
        <div className="p-4 border-t border-sidebar-border/30">
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-harvest-primary/10 border border-harvest-primary/20">
            <div className="h-2 w-2 rounded-full bg-harvest-primary animate-pulse" />
            <span className="text-xs font-medium text-harvest-primary">System Online</span>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
